import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  StatusBadge,
  Eyebrow,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  type StatusKey,
} from '@insolech/ui';
import { Eye, Pencil, ArrowRight } from 'lucide-react';
import type { OrderDetail } from '@insolech/sdk';
import { useAuth } from '@/features/auth/AuthContext';
import {
  PrescriptionForm,
  type PrescriptionFormState,
  type PrescriptionModification,
} from './wizard/PrescriptionForm';
import { EditPrescriptionDialog } from './EditPrescriptionDialog';
import { EditOrderLinePriceDialog } from './EditOrderLinePriceDialog';
import type {
  InsoleDesignState,
  PrescriptionDeepState,
} from './wizard/prescription/prescriptionState';
import {
  summarizeModifications,
} from './wizard/prescription/summary';
import { insoleTopCoverFor } from './isSandalLine';
import { shortProductLabel, solutionBranchLabel } from './productLabel';

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

/**
 * The PrescriptionDrawer below is always rendered read-only — the new
 * EditPrescriptionDialog (opened by the "Edit prescription" button) is
 * the mutation path for both `draft` and `submitted` orders. The drawer
 * keeps its informational role: a full read-only print of the
 * prescription with the locked-status banner once the order is no
 * longer in draft.
 *
 * Statuses where the "Edit prescription" dialog is allowed for
 * non-admin podiatrists. Per the order-intake spec the entry point
 * disappears once the order moves to in_production or later — admins
 * can still edit at any status via the same dialog.
 */
const EDITABLE_STATUSES_FOR_PODIATRIST = new Set<string>(['draft', 'submitted']);

function canEditPrescription(
  orderStatus: string | null | undefined,
  lineStatus: string | null | undefined,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  if (!orderStatus || !lineStatus) return false;
  return (
    EDITABLE_STATUSES_FOR_PODIATRIST.has(orderStatus) &&
    EDITABLE_STATUSES_FOR_PODIATRIST.has(lineStatus)
  );
}

interface Props {
  order: OrderDetail;
  /**
   * When false, hide the "Price: AED X x N = AED Y" row entirely (the
   * admin-only pencil button is gated separately by isAdmin, which is a
   * strictly stronger condition than canViewPrices, so it disappears
   * automatically when this flag is false).
   *
   * The caller (OrderDetailPage) derives this from useAuth + the order's
   * direct-billing status. Defaults to false so legacy / out-of-tree
   * callers do not accidentally leak prices.
   */
  canViewPrices?: boolean;
}

/**
 * Order summary card.
 *
 * Single-line-per-order is the design contract - reorders create a new
 * order with its own ID rather than appending lines. So this card flattens
 * to a single summary block (no Line tabs) and surfaces the same shape as
 * the wizard's Review step: product + top cover + filled modifications.
 *
 * Older orders (pre-R28) won't have `configuration.deep`; we fall back to
 * the persisted modifications + specs in that case so legacy orders still
 * render something useful.
 */
export function OrderLinesCard({ order, canViewPrices = false }: Props) {
  const { t } = useTranslation('orders');
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [priceEditOpen, setPriceEditOpen] = React.useState(false);
  const primaryLine = order.lines[0];

  if (!primaryLine) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-text-secondary">{t('lines_summary_empty')}</p>
        </CardBody>
      </Card>
    );
  }

  // The drawer is always read-only now - editing happens in the dedicated
  // EditPrescriptionDialog below, which is the new path for mutating a
  // submitted prescription. The drawer keeps its informational role
  // (full prescription details, locked-status notice).

  // Edit-prescription button gating: admins always, podiatrists only
  // while order/line are in the editable-statuses set.
  const canEdit = canEditPrescription(order.status, primaryLine.status, isAdmin);
  // Item 7: a DRAFT continues in the STANDARD new-order wizard (resume), not
  // the Update-Prescription dialog. Submitted+ orders keep the dialog.
  const isDraft = order.status === 'draft';

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Order summary</CardTitle>
        </CardHeader>
        <CardBody>
          <LineSummary
            line={primaryLine}
            patientNotes={order.patient?.notes ?? null}
            canEdit={canEdit}
            isDraft={isDraft}
            canViewPrice={canViewPrices}
            canEditPrice={isAdmin}
            onOpenPrescription={() => setDrawerOpen(true)}
            onEditPrescription={() => setEditOpen(true)}
            onContinueInWizard={() => navigate(`/orders/new?draft=${order.id}`)}
            onEditPrice={() => setPriceEditOpen(true)}
          />
        </CardBody>
      </Card>

      {drawerOpen ? (
        <PrescriptionDrawer
          open={drawerOpen}
          line={primaryLine}
          readOnly
          onClose={() => setDrawerOpen(false)}
        />
      ) : null}

      {editOpen ? (
        <EditPrescriptionDialog
          open={editOpen}
          order={order}
          line={primaryLine}
          onClose={() => setEditOpen(false)}
          canViewPrices={canViewPrices}
        />
      ) : null}

      {priceEditOpen ? (
        <EditOrderLinePriceDialog
          open={priceEditOpen}
          order={order}
          line={primaryLine}
          onClose={() => setPriceEditOpen(false)}
        />
      ) : null}
    </>
  );
}

interface LineSummaryProps {
  line: OrderDetail['lines'][number];
  /** Patient notes = `patients.notes`, surfaced on the detail query as
   *  `order.patient.notes` (the same field the prescription Excel's PATIENT
   *  section prints as "Patient notes"). Null for record-less orders. */
  patientNotes: string | null;
  canEdit: boolean;
  /** Item 7: order is a draft -> the edit CTA becomes "Continue in wizard". */
  isDraft: boolean;
  /** Show the Price row (unit price x qty = line total). Hidden for
   *  non-admin viewers unless they authored a direct-billed order. */
  canViewPrice: boolean;
  /** Admin-only: surface the pencil-icon Edit-price affordance next to the
   *  line price. Implies canViewPrice (admins always see prices), but the
   *  parent passes both explicitly for clarity. */
  canEditPrice: boolean;
  onOpenPrescription: () => void;
  onEditPrescription: () => void;
  onContinueInWizard: () => void;
  onEditPrice: () => void;
}

function LineSummary({
  line,
  patientNotes,
  canEdit,
  isDraft,
  canViewPrice,
  canEditPrice,
  onOpenPrescription,
  onEditPrescription,
  onContinueInWizard,
  onEditPrice,
}: LineSummaryProps) {
  const { t } = useTranslation('orders');
  const config = (line.configuration ?? {}) as Record<string, unknown>;
  const deep = (config.deep as PrescriptionDeepState | undefined) ?? undefined;
  // null for a sandal: sandal lines store the wizard's default insole design
  // branch, and a sandal never shows an insole top cover (see isSandalLine.ts).
  const topCover = insoleTopCoverFor(line, deep?.design as InsoleDesignState | undefined);
  const mods = summarizeModifications(deep);
  // Field mapping (kept in lockstep with prescriptionExcelExport.ts):
  // podiatrist observations = the line-level `configuration.observations`
  // ("Notes for the lab", written by the prescription form and printed in the
  // Excel's PODIATRIST OBSERVATIONS section) - deliberately NOT
  // orders.admin_observations, which is the admin's own order-level field.
  const observations = typeof config.observations === 'string' ? config.observations.trim() : '';
  const patientNote = patientNotes?.trim() ?? '';
  // Custom name (personalisation lasered/printed on the shell): insoles store
  // it at deep.design.custom_name, sandals at deep.sandal.custom_name.
  const customName = (deep?.design?.custom_name ?? deep?.sandal?.custom_name ?? '').trim();
  // Design remarks: the free text the podiatrist writes for the lab in the
  // design step. Same dual home as custom_name (insoles deep.design, sandals
  // deep.sandal), and the label mirrors the lab sheet, which prints it as
  // "Refurbishment remarks" on refurbishment orders (prescriptionExcelExport
  // relabels on subtype === 'insoles_refurbishment').
  const designRemarks = (deep?.design?.design_remarks ?? deep?.sandal?.design_remarks ?? '').trim();
  const designRemarksLabel =
    line.solution_subtype === 'insoles_refurbishment' ? 'Refurbishment remarks' : 'Design remarks';

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Eyebrow>
            {solutionBranchLabel(line.line_type)} · {shortProductLabel(line)}
          </Eyebrow>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge
            status={(STATUS_TO_KEY[line.status] ?? 'draft') as StatusKey}
            size="sm"
          />
          {line.assigned_provider ? (
            <span className="text-xs text-text-muted">
              Assigned to <strong>{line.assigned_provider.name}</strong>
            </span>
          ) : null}
        </div>
      </div>

      {topCover !== null || customName || designRemarks ? (
        <SummarySection title="Product">
          {topCover !== null ? <SummaryRow label="Top cover" value={topCover} /> : null}
          {customName ? <SummaryRow label="Custom name" value={customName} /> : null}
          {designRemarks ? <SummaryNoteRow label={designRemarksLabel} text={designRemarks} /> : null}
        </SummarySection>
      ) : null}

      <SummarySection title="Modifications">
        {mods.length === 0 ? (
          <p className="text-sm text-text-muted">{t('lines_no_modifications')}</p>
        ) : (
          <ul className="space-y-1.5">
            {mods.map((m) => (
              <li key={m.label} className="text-sm">
                <span className="font-medium text-text-primary">{m.label}:</span>{' '}
                <span className="text-text-secondary">{m.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </SummarySection>

      {/* Free-text notes, rendered only when present (same optional-row
          contract as the provider-assignment label above). Both clamp to two
          lines so a long note signals its existence without swallowing the
          card; the full text stays one hover (title) or one "View full
          prescription" away. */}
      {patientNote || observations ? (
        <SummarySection title="Notes">
          {patientNote ? <SummaryNoteRow label="Patient notes" text={patientNote} /> : null}
          {observations ? (
            <SummaryNoteRow label="Podiatrist observations" text={observations} />
          ) : null}
        </SummarySection>
      ) : null}

      <div className="pt-3 border-t border-border-subtle flex flex-wrap items-center justify-between gap-3">
        {canViewPrice ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-text-muted">Price:</span>
            <span className="tabular text-text-primary">
              AED {line.unit_price_aed.toFixed(2)} × {line.quantity}
            </span>
            <span className="text-text-muted">=</span>
            <span className="tabular font-medium text-text-primary">
              AED {line.line_total_aed.toFixed(2)}
            </span>
            {canEditPrice ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={onEditPrice}
                aria-label="Edit line price"
                title="Edit price (admin)"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenPrescription}
            startIcon={<Eye className="h-4 w-4" />}
          >
            {t('lines_view_full_prescription')}
          </Button>
          {canEdit ? (
            isDraft ? (
              <Button
                variant="primary"
                size="sm"
                onClick={onContinueInWizard}
                endIcon={<ArrowRight className="h-4 w-4" />}
                data-testid="order-continue-in-wizard"
              >
                Continue in wizard
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={onEditPrescription}
                startIcon={<Pencil className="h-4 w-4" />}
              >
                Edit prescription
              </Button>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="w-28 shrink-0 text-text-muted">{label}</span>
      <span className="text-text-primary">{value}</span>
    </div>
  );
}

/** SummaryRow variant for long free text: clamps the value to two lines so
 *  the reader can tell text exists without the card growing unbounded. The
 *  title attribute carries the full text on hover. */
function SummaryNoteRow({ label, text }: { label: string; text: string }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="w-28 shrink-0 text-text-muted">{label}</span>
      <span className="min-w-0 whitespace-pre-line line-clamp-2 text-text-primary" title={text}>
        {text}
      </span>
    </div>
  );
}

function PrescriptionDrawer({
  open,
  line,
  readOnly,
  onClose,
}: {
  open: boolean;
  line: OrderDetail['lines'][number];
  /**
   * Locks every field in the embedded PrescriptionForm. Driven by the
   * order/line status via `isOrderLocked` — anything past `draft` flips
   * this on for non-admin viewers. Defaults to `true` so legacy callers
   * keep the safe, read-only behaviour they had before the lock landed.
   */
  readOnly?: boolean;
  onClose: () => void;
}) {
  const isReadOnly = readOnly ?? true;
  // Hydrate a PrescriptionFormState from the persisted line.configuration + line.modifications.
  //
  // Parity contract: this MUST stay in lockstep with
  // EditPrescriptionDialog.hydrateFormState. Any field the edit dialog
  // restores onto the form (so the operator sees what they previously
  // saved) the read-only drawer must restore too, otherwise the "View
  // full prescription" button shows a blanker prescription than the
  // "Edit prescription" dialog opens.
  //
  // R28+ moved every per-region detail (insole_cup, design_remarks,
  // custom_name, all rearfoot / midfoot / forefoot / offloadings cards
  // and the sandal configurator) under `configuration.deep`. Hydrating
  // just the legacy top-level keys (material / cover / size / etc.) left
  // every deep field empty in the drawer.
  const value = React.useMemo<PrescriptionFormState>(() => {
    const cfg = (line.configuration ?? {}) as Record<string, unknown>;
    const sizeNode = cfg.size as
      | { left?: { length_mm?: number; width_mm?: number }; right?: { length_mm?: number; width_mm?: number } }
      | undefined;
    const next: PrescriptionFormState = {
      line_type: (line.line_type as PrescriptionFormState['line_type']) ?? 'custom_insole',
      modifications: extractModifications(line),
    };
    if (line.product?.id) next.product_id = line.product.id;
    if (typeof cfg.material === 'string') next.material = cfg.material;
    if (typeof cfg.thickness_mm === 'number') next.thickness_mm = cfg.thickness_mm;
    if (typeof cfg.cover === 'string') next.cover = cfg.cover;
    if (typeof cfg.pathology === 'string') next.pathology = cfg.pathology;
    if (typeof cfg.observations === 'string') next.observations = cfg.observations;
    if (sizeNode?.left?.length_mm) next.size_left_length_mm = sizeNode.left.length_mm;
    if (sizeNode?.right?.length_mm) next.size_right_length_mm = sizeNode.right.length_mm;
    if (sizeNode?.left?.width_mm) next.size_left_width_mm = sizeNode.left.width_mm;
    if (sizeNode?.right?.width_mm) next.size_right_width_mm = sizeNode.right.width_mm;
    // R28 deep prescription state: every field rendered by
    // InsoleDesignSection / RearfootSection / MidfootSection /
    // ForefootSection / OffloadingsSection / SandalStyleSection lives
    // under cfg.deep. structuredClone so a future writable drawer can
    // mutate freely without touching the cached query data.
    const deep = cfg.deep as Record<string, unknown> | undefined;
    if (deep) {
      const cloned = deepClone(deep) as NonNullable<PrescriptionFormState['deep']>;
      next.deep = cloned;
    }
    return next;
  }, [line]);

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()}>
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle>Prescription · Line {line.line_number}</ModalTitle>
        </ModalHeader>
        <ModalBody className="max-h-[75vh] overflow-y-auto">
          {isReadOnly ? (
            <Alert
              tone="info"
              title="Order locked for editing"
              className="mb-4"
            >
              This order has been submitted, so the prescription can no longer
              be changed from here. To request a change, contact your INSOLECH
              provider.
            </Alert>
          ) : null}
          <PrescriptionForm
            value={value}
            onChange={() => undefined}
            readOnly={isReadOnly}
            {...(line.solution_subtype
              ? { subtype: line.solution_subtype as React.ComponentProps<typeof PrescriptionForm>['subtype'] }
              : {})}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Extractors                                                                */
/* -------------------------------------------------------------------------- */

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

function extractModifications(
  line: OrderDetail['lines'][number],
): PrescriptionModification[] {
  const raw = (line.configuration as Record<string, unknown>)?.modifications;
  const fromLine = line.modifications as unknown;
  const candidate = Array.isArray(fromLine) && fromLine.length > 0 ? fromLine : raw;
  if (!Array.isArray(candidate)) return [];
  const out: PrescriptionModification[] = [];
  for (const m of candidate) {
    const item = m as Record<string, unknown>;
    const region = item.region as string | undefined;
    const modification_type = item.modification_type as string | undefined;
    if (!region || !modification_type) continue;
    const intensity = item.intensity as PrescriptionModification['intensity'];
    const next: PrescriptionModification = {
      region: region as PrescriptionModification['region'],
      modification_type,
    };
    if (intensity) next.intensity = intensity;
    if (item.parameters && typeof item.parameters === 'object') {
      next.parameters = item.parameters as Record<string, unknown>;
    }
    out.push(next);
  }
  return out;
}

